import { defineStore } from 'pinia'
import { useAppStore } from './appStore'
import { api } from '../services/api'
import router from '../router'
import { useToast } from 'vuestic-ui'

const TOKEN_STORAGE_KEY = 'token@lifeadmin'
const USER_STORAGE_KEY = 'user@lifeadmin'

type AdminUser = {
  id?: number
  name?: string
  email?: string
  email_verified_at?: string | null
  role?: string
  created_at?: string | null
  updated_at?: string | null
}

type UpdatePasswordPayload = {
  password: string
  password_confirmation: string
}

const roleLabels: Record<string, string> = {
  admin: 'Administrador',
  editor: 'Editor',
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const readStoredUser = (): AdminUser => {
  const storedUser = localStorage.getItem(USER_STORAGE_KEY)

  if (!storedUser) {
    return {}
  }

  try {
    const parsedUser = JSON.parse(storedUser)

    return isRecord(parsedUser) ? parsedUser : {}
  } catch {
    return {}
  }
}

const extractUser = (payload: unknown): AdminUser | null => {
  if (!isRecord(payload)) {
    return null
  }

  if (isRecord(payload.user)) {
    return payload.user
  }

  if ('id' in payload || 'name' in payload || 'email' in payload) {
    return payload
  }

  return null
}

const getResponseMessage = (payload: unknown, fallback: string) => {
  return isRecord(payload) && typeof payload.message === 'string' ? payload.message : fallback
}

const getErrorMessage = (error: any) => {
  const data = error?.response?.data

  if (isRecord(data?.errors)) {
    const firstFieldErrors = Object.values(data.errors)[0]

    if (Array.isArray(firstFieldErrors) && typeof firstFieldErrors[0] === 'string') {
      return firstFieldErrors[0]
    }
  }

  if (typeof data?.message === 'string') {
    return data.message
  }

  return error?.message || 'Erro inesperado'
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return new Intl.DateTimeFormat('pt-BR').format(date)
}

export const useUserStore = defineStore('user', {
  state: () => ({
    user: readStoredUser(),
    token: localStorage.getItem(TOKEN_STORAGE_KEY) || '',
  }),

  getters: {
    userName: (state) => state.user.name || 'Usuário',
    email: (state) => state.user.email || '-',
    roleLabel: (state) => (state.user.role ? roleLabels[state.user.role] || state.user.role : '-'),
    memberSince: (state) => formatDate(state.user.created_at),
    userInitials: (state) => {
      const name = state.user.name?.trim()

      if (!name) {
        return 'FL'
      }

      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('')
    },
  },

  actions: {
    persistUser(user: AdminUser) {
      this.user = user
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user))
    },
    async signIn(formData: { email: string; password: string }) {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.post(`/auth/admin/sign-in`, {
          email: formData.email,
          password: formData.password,
        })

        const user = extractUser(response.data)

        if (user) {
          this.persistUser(user)
        }

        this.token = response.data.token

        localStorage.setItem(TOKEN_STORAGE_KEY, response.data.token)

        await router.push({ name: 'dashboard' })
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })
      } finally {
        appStore.toggleLoading(false)
      }
    },
    async getProfile() {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.get('/auth/admin/me')
        const user = extractUser(response.data)

        if (user) {
          this.persistUser(user)
        }
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })
      } finally {
        appStore.toggleLoading(false)
      }
    },
    async updatePassword(formData: UpdatePasswordPayload) {
      const appStore = useAppStore()

      appStore.toggleLoading(true)
      try {
        const response = await api.put('/auth/admin/profile', {
          password: formData.password,
          password_confirmation: formData.password_confirmation,
        })
        const user = extractUser(response.data)

        if (user) {
          this.persistUser(user)
        }

        const { init } = useToast()
        init({ message: getResponseMessage(response.data, 'Senha atualizada com sucesso!'), color: 'success' })

        return true
      } catch (e: any) {
        const { init } = useToast()
        init({ message: getErrorMessage(e), color: 'danger' })

        return false
      } finally {
        appStore.toggleLoading(false)
      }
    },
    async logout() {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      localStorage.removeItem(USER_STORAGE_KEY)
      this.user = {}
      this.token = ''

      await router.push({ name: 'login' })
    },
  },
})
